import { useState } from 'react';
import {
  batteryLabel,
  batteryState,
  connState,
  dedupeSlots,
  gpsLabel,
  gpsState,
  type StatusSlot,
} from '../../shared/playerStatus';
import { readinessReason } from '../../shared/readiness';

/**
 * v22 BOLT-116 — every player's live device status: connection, GPS, battery.
 *
 * Connection and GPS were already on the wire (v19 BOLT-087) but only fed the
 * host's start-gate; now both players can see, in the lobby AND mid-round,
 * whether their partner is connected, precise, and about to run out of phone.
 */
/** Inline icons: nothing fetched, and they inherit currentColor. */
function SignalIcon({ className, title }: { className: string; title: string }) {
  return (
    <svg className={`pstat__ico ${className}`} width="11" height="11" viewBox="0 0 12 12" aria-label={title}>
      <title>{title}</title>
      <rect x="0.5" y="7" width="2" height="4" rx="0.5" fill="currentColor" />
      <rect x="4" y="4.5" width="2" height="6.5" rx="0.5" fill="currentColor" />
      <rect x="7.5" y="1.5" width="2" height="9.5" rx="0.5" fill="currentColor" />
    </svg>
  );
}
function GpsIcon({ className, title }: { className: string; title: string }) {
  return (
    <svg className={`pstat__ico ${className}`} width="11" height="11" viewBox="0 0 12 12" aria-label={title}>
      <title>{title}</title>
      <path d="M6 0.8 L10.6 11.2 L6 8.9 L1.4 11.2 Z" fill="currentColor" />
    </svg>
  );
}
/**
 * v28 BOLT-154 (Jake): "ensure the spacing between the battery indicator and
 * the battery percentage value is consistent with the others."
 *
 * It was not, and the gap was not the culprit - `.pstat__cell` has always used
 * one gap for all three. The ICON BOX was: battery drew into a 16-wide viewBox
 * at 14 px while signal and GPS drew into a 12-wide one at 11 px, and the two
 * glyphs ended at different fractions of their boxes. So the battery is redrawn
 * here into the SAME 12x12 box, ending at the same x (10.6) as the GPS arrow.
 * Identical boxes, identical trailing slack, identical gap.
 */
function BatteryIcon({ className, title }: { className: string; title: string }) {
  return (
    <svg className={`pstat__ico ${className}`} width="11" height="11" viewBox="0 0 12 12" aria-label={title}>
      <title>{title}</title>
      <rect x="0.55" y="3.2" width="8.6" height="5.6" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.1" />
      <rect x="9.7" y="4.9" width="0.9" height="2.2" rx="0.45" fill="currentColor" />
    </svg>
  );
}

/**
 * The three readouts, with no name attached.
 *
 * v28 BOLT-153 splits this out because the lobby now renders status INSIDE the
 * Orange and White team containers, where the tile already says whose it is.
 * Repeating the name there would be the duplication the ticket exists to
 * remove. One implementation of the readouts, two framings.
 */
export function PlayerStatusCells({ slot }: { slot: StatusSlot }) {
  const gps = gpsState(slot);
  const conn = connState(slot);
  const battery = batteryState(slot);
  const why = readinessReason(slot);
  return (
    <>
      {/* v24, Jake in the field: the icons were colour-coded AND only one of
          them carried a value, which read as three unexplained lights next to a
          percentage. Now every icon is NEUTRAL and every icon has a readout
          beside it. Connection is the one with no meaningful number, so it
          keeps the colour-coded dot. */}
      <span className="pstat__cell" title={conn}>
        <SignalIcon className="" title={conn} />
        <i className={`pstat__dot pstat__dot--${conn === 'connected' ? 'ok' : 'warn'}`} aria-hidden="true" />
      </span>
      <span className="pstat__cell" title={why ?? 'GPS accuracy'}>
        <GpsIcon className="" title="GPS" />
        <span className={`pstat__val pstat__val--${gps === 'precise' ? 'ok' : gps === 'waiting' ? 'warn' : 'bad'}`} data-testid="pstat-gps">
          {gpsLabel(slot)}
        </span>
      </span>
      <span className="pstat__cell" title={battery === 'unknown' ? 'Battery is not readable in this browser' : 'Battery'}>
        <BatteryIcon className="" title="Battery" />
        <span className={`pstat__val pstat__val--${battery}`} data-testid="pstat-battery">
          {batteryLabel(slot)}
        </span>
      </span>
    </>
  );
}

export function PlayerStatusRow({ slot, you }: { slot: StatusSlot; you?: boolean }) {
  return (
    <div className="pstat" data-testid={`pstat-${slot.name.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="pstat__who">
        {slot.name}
        {you ? ' (you)' : ''}
      </span>
      <PlayerStatusCells slot={slot} />
    </div>
  );
}

/**
 * The in-game strip. Collapsible, and collapsed by default, so it never
 * clutters the map: the cockpit is for playing, not for monitoring.
 */
export function PlayerStatusStrip({ slots }: { slots: { slot: StatusSlot; you?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  // Dedupe HERE as well as at the call site: `players` exposes the same person
  // as both a role and a team, and that is exactly how the duplicate rows got in.
  const rows = dedupeSlots(slots.map((s) => ({ ...s.slot, __you: s.you })));
  if (!rows.length) return null;
  return (
    <div className="pstat-strip" data-testid="player-status-strip" data-open={open}>
      <button className="pstat-strip__toggle" data-testid="player-status-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? '▾ Status' : '▸ Status'}
      </button>
      {open && (
        <div className="pstat-strip__rows">
          {rows.map((row) => (
            <PlayerStatusRow key={row.name} slot={row} you={row.__you} />
          ))}
        </div>
      )}
    </div>
  );
}
